<template>
  <div class="team-sprint-detail-panel">
    <div v-if="loading" class="text-secondary text-sm py-4">
      Loading sprint calendar...
    </div>

    <div v-else-if="error" class="alert alert-danger text-sm py-2" role="alert">
      {{ error }}
    </div>

    <template v-else>
      <div class="detail-panel-layout">
        <div class="detail-card detail-card--calendar card">
          <div class="card-body p-3 p-md-4">
            <h6 v-if="heading" class="detail-panel-heading mb-3">{{ heading }}</h6>
            <team-sprint-calendar
              :team="team"
              :start-date="startDate"
              :end-date="endDate"
              :week-count="weekCount"
              :range-mode="rangeMode"
              :tasks="tasks"
              :label-suffix="labelSuffix"
            />
          </div>
        </div>

        <div class="detail-card detail-card--overdue card">
          <div class="card-body p-3 detail-card__body">
            <h6 class="detail-card__title mb-3">Overdue</h6>

            <div class="detail-card__scroll">
              <div v-if="!overdueTasks.length" class="text-secondary text-sm">
                No overdue tasks in this sprint.
              </div>

              <ul v-else class="milestone-list mb-0">
                <li
                  v-for="item in overdueTasks"
                  :key="item.key"
                  class="milestone-item"
                >
                  <span class="milestone-item__dot milestone-item__dot--overdue"></span>
                  <button
                    type="button"
                    class="milestone-item__body milestone-item__body--button"
                    :disabled="!item.taskId"
                    @click="openOverdueDetail(item)"
                  >
                    <span class="milestone-item__label">{{ overdueTitle(item) }}</span>
                    <span class="milestone-item__assign">{{ item.assignee || "Unassigned" }}</span>
                    <span class="milestone-item__date">{{ formatDate(item.dueDate) }}</span>
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </template>

    <test-plan-tab
      v-if="previewTaskId"
      hide-shell
      view-only
      :open-task-id="previewTaskId"
      @close="previewTaskId = null"
    />
  </div>
</template>

<script>
import TeamSprintCalendar from "./TeamSprintCalendar.vue";
import TestPlanTab from "./tabs/TestPlanTab.vue";

export default {
  name: "TeamSprintDetailPanel",
  components: { TeamSprintCalendar, TestPlanTab },
  props: {
    team: { type: String, required: true },
    heading: { type: String, default: "" },
    loading: { type: Boolean, default: false },
    error: { type: String, default: null },
    startDate: { type: String, default: null },
    endDate: { type: String, default: null },
    weekCount: { type: Number, default: null },
    rangeMode: { type: String, default: "sprint" },
    tasks: { type: Array, default: () => [] },
    milestones: { type: Array, default: () => [] },
    overdueTasks: { type: Array, default: () => [] },
    labelSuffix: { type: String, default: "" },
  },
  data() {
    return {
      previewTaskId: null,
    };
  },
  methods: {
    overdueTitle(item) {
      const id = item?.taskId != null && item.taskId !== "" ? `#${item.taskId}` : "";
      const name = String(item?.title || "").trim();
      if (!id) return name || "Untitled task";
      if (name.toLowerCase().startsWith(id.toLowerCase())) return name;
      return name ? `${id} ${name}` : id;
    },
    formatDate(val) {
      if (!val) return "—";
      const d = new Date(val);
      if (Number.isNaN(d.getTime())) return "—";
      return d.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    },
    openOverdueDetail(item) {
      const taskId = Number(item?.taskId);
      if (!Number.isFinite(taskId)) return;
      this.previewTaskId = taskId;
    },
  },
};
</script>

<style scoped>
.team-sprint-detail-panel {
  flex: 0 0 auto;
  min-height: auto;
  height: auto;
  display: flex;
  flex-direction: column;
}

.detail-panel-heading {
  font-size: 0.85rem;
  font-weight: 700;
  color: #344767;
}

.detail-panel-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(240px, 300px);
  gap: 1rem;
  align-items: stretch;
}

.detail-card--calendar {
  min-width: 0;
}

.detail-card--overdue {
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-self: stretch;
}

.detail-card--overdue .detail-card__body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.detail-card__title {
  flex-shrink: 0;
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--text-primary, #344767);
}

.detail-card__scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

.detail-card {
  border-radius: var(--radius-card, 12px) !important;
  border: 1px solid var(--surface-border, #e9ecef);
  box-shadow: var(--shadow-card);
  background: var(--surface-card, #fff);
}

.milestone-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.milestone-item {
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
}

.milestone-item__dot {
  width: 0.55rem;
  height: 0.55rem;
  border-radius: 50%;
  margin-top: 0.35rem;
  flex-shrink: 0;
  background: var(--text-secondary, #67748e);
}

.milestone-item__dot--overdue {
  background: #e74c3c;
}

.milestone-item__body {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
}

.milestone-item__body--button {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 0;
  text-align: left;
  cursor: pointer;
}

.milestone-item__body--button:hover .milestone-item__label,
.milestone-item__body--button:focus-visible .milestone-item__label {
  color: #5e72e4;
  text-decoration: underline;
}

.milestone-item__body--button:disabled {
  cursor: default;
}

.milestone-item__label {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text-primary, #344767);
  word-break: break-word;
}

.milestone-item__assign {
  font-size: 0.72rem;
  color: #5e72e4;
}

.milestone-item__date {
  font-size: 0.72rem;
  color: var(--text-secondary, #67748e);
}

@media (max-width: 991.98px) {
  .detail-panel-layout {
    grid-template-columns: 1fr;
  }

  .detail-card--overdue {
    min-height: 220px;
    max-height: 320px;
  }
}
</style>
